class PromoDiscountDrawer extends HTMLElement {
  static STORAGE_CODE_APPLIED = 'promo_discount_drawer_code_applied';

  connectedCallback() {
    this.delayMs = (parseInt(this.dataset.delay, 10) || 3) * 1000;
    this.discountCode = this.dataset.discountCode || '';
    this.autoOpenHomepage = this.dataset.autoOpenHomepage === 'true';
    this.strings = window.promoDiscountDrawerStrings || {};

    this.tab = this.querySelector('.promo-discount-drawer__tab');
    this.panel = this.querySelector('.promo-discount-drawer__panel');
    this.closeBtn = this.querySelector('.promo-discount-drawer__close');
    this.codeBtn = this.querySelector('.promo-discount-drawer__code');
    this.feedback = this.querySelector('.promo-discount-drawer__feedback');

    this.bindEvents();
    this.initState();
  }

  bindEvents() {
    this.tab?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.minimize());
    this.codeBtn?.addEventListener('click', () => this.handleCodeClick());

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        event.preventDefault();
        this.minimize();
      }
    });
  }

  initState() {
    if (this.isCodeApplied()) {
      this.setHidden();
      return;
    }

    this.showMinimized();

    if (this.autoOpenHomepage) {
      this.scheduleAutoOpen();
      this.listenForBlockers();
    }
  }

  listenForBlockers() {
    document.addEventListener('city:changed', () => {
      if (!this.autoOpenHomepage) return;
      if (!this.isCodeApplied() && !this.classList.contains('is-open')) {
        this.scheduleAutoOpen();
      }
    });
  }

  isCodeApplied() {
    const stored = localStorage.getItem(PromoDiscountDrawer.STORAGE_CODE_APPLIED);
    const code = (this.discountCode || this.codeBtn?.dataset.code || '').toUpperCase();
    return Boolean(stored && code && stored === code);
  }

  persistCodeApplied(code) {
    localStorage.setItem(PromoDiscountDrawer.STORAGE_CODE_APPLIED, code.toUpperCase());
  }

  setHidden() {
    this.classList.remove('is-open', 'is-minimized');
    this.classList.add('is-hidden');
    this.hidden = true;
    this.updateAria(false);
  }

  showMinimized() {
    this.hidden = false;
    this.classList.remove('is-open', 'is-hidden');
    this.classList.add('is-minimized');
    this.updateAria(false);
  }

  open() {
    this.hidden = false;
    this.classList.remove('is-hidden', 'is-minimized');
    this.classList.add('is-open');
    this.updateAria(true);
    this.closeBtn?.focus();
  }

  minimize() {
    this.showMinimized();
    this.tab?.focus();
  }

  updateAria(isPanelOpen) {
    if (this.panel) {
      this.panel.setAttribute('aria-hidden', isPanelOpen ? 'false' : 'true');
    }
    if (this.tab) {
      this.tab.setAttribute('aria-expanded', isPanelOpen ? 'true' : 'false');
    }
  }

  canAutoOpen() {
    if (!this.autoOpenHomepage) return false;
    if (this.isCodeApplied()) return false;

    if (document.querySelector('.city--modal--active')) return false;

    const newsletterPopup = document.getElementById('newsletter-popup');
    if (newsletterPopup && window.getComputedStyle(newsletterPopup).display === 'flex') {
      return false;
    }

    return true;
  }

  scheduleAutoOpen() {
    if (!this.autoOpenHomepage) return;

    if (this.autoOpenTimer) {
      window.clearTimeout(this.autoOpenTimer);
    }

    const attemptOpen = () => {
      if (!this.canAutoOpen()) {
        this.autoOpenTimer = window.setTimeout(attemptOpen, 500);
        return;
      }

      this.autoOpenTimer = window.setTimeout(() => {
        if (this.canAutoOpen() && !this.classList.contains('is-open')) {
          this.open();
        }
      }, this.delayMs);
    };

    attemptOpen();
  }

  dismissAfterApply() {
    if (this.applyHideTimer) {
      window.clearTimeout(this.applyHideTimer);
    }

    this.applyHideTimer = window.setTimeout(() => {
      this.setHidden();
    }, 2000);
  }

  disconnectedCallback() {
    if (this.autoOpenTimer) {
      window.clearTimeout(this.autoOpenTimer);
    }
    if (this.applyHideTimer) {
      window.clearTimeout(this.applyHideTimer);
    }
  }

  showFeedback(message, type) {
    if (!this.feedback) return;

    this.feedback.textContent = message;
    this.feedback.hidden = false;
    this.feedback.classList.remove('is-success', 'is-error');
    this.feedback.classList.add(type === 'success' ? 'is-success' : 'is-error');
  }

  resetCodeButtonState() {
    this.codeBtn?.classList.remove('is-success', 'is-error');
  }

  async copyCode(code) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(code);
      return true;
    }

    const textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }

    return copied;
  }

  async applyDiscount(code) {
    if (window.TwinkleCart?.applyDiscountCode) {
      return window.TwinkleCart.applyDiscountCode(code);
    }

    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ discount: code })
    }).then((res) => res.json());

    const applied = (response.cart_level_discount_applications || []).some(
      (discount) => discount.title.toUpperCase() === code.toUpperCase()
    );

    return { cart: response, applied };
  }

  async handleCodeClick() {
    const code = this.codeBtn?.dataset.code || this.discountCode;
    if (!code) return;

    this.resetCodeButtonState();
    this.feedback.hidden = true;

    let copied = false;
    let applied = false;

    try {
      copied = await this.copyCode(code);
      const result = await this.applyDiscount(code);
      applied = result?.applied;

      if (applied) {
        this.persistCodeApplied(code);
        this.codeBtn?.classList.add('is-success');

        if (copied) {
          this.showFeedback(this.strings.copiedAndApplied || 'Code copied and applied!', 'success');
        } else {
          this.showFeedback(this.strings.applied || 'Discount applied!', 'success');
        }

        this.dismissAfterApply();
      } else if (copied) {
        this.codeBtn?.classList.add('is-success');
        this.showFeedback(this.strings.copied || 'Code copied!', 'success');
      } else {
        this.codeBtn?.classList.add('is-error');
        this.showFeedback(this.strings.error || 'Could not apply discount code.', 'error');
      }
    } catch (error) {
      console.error('PromoDiscountDrawer:', error);
      this.codeBtn?.classList.add('is-error');
      this.showFeedback(this.strings.error || 'Could not apply discount code.', 'error');
    }
  }
}

if (!customElements.get('promo-discount-drawer')) {
  customElements.define('promo-discount-drawer', PromoDiscountDrawer);
}
